import { useCallback, useState } from "react";
import { useCreatePrebuild } from "@/queries/prebuild-queries";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { Button } from "@/components/flexkit/Button";
import { useNavigate } from "react-router-dom";

export const useTriggerPrebuild = (projectId: string | undefined, environmentClassIds: string[]) => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const createPrebuild = useCreatePrebuild();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const trigger = useCallback(async () => {
        if (!projectId) return;

        if (environmentClassIds.length === 0) {
            toast({
                title: "No prebuild environment classes configured",
                description: "Please configure environment classes for this project to trigger prebuilds",
            });
            return;
        }

        try {
            setIsSubmitting(true);
            for (const classId of environmentClassIds) {
                await createPrebuild.mutateAsync({ projectId, environmentClassId: classId });
            }
            const message = environmentClassIds.length > 1 ? "Prebuilds triggered" : "Prebuild triggered";
            const handle = toast({
                title: message,
                action: (
                    <Button
                        onClick={() => {
                            navigate(`/projects/${projectId}/prebuilds`);
                            handle.dismiss();
                        }}
                        data-tracking-id="view-prebuilds-prebuild-button"
                    >
                        View prebuilds
                    </Button>
                ),
            });
        } catch (error: unknown) {
            toast({ title: "Could not trigger prebuild", description: formatError(error) });
        } finally {
            setIsSubmitting(false);
        }
    }, [projectId, environmentClassIds, createPrebuild, toast, navigate]);

    return { trigger, isSubmitting };
};
